import { supabase } from "@/lib/supabase";
import { Session, User } from "@supabase/supabase-js";
import { useRouter, useSegments } from "expo-router";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import * as Linking from 'expo-linking';
import { Platform } from "react-native";
import { getBiometricAvailability, type BiometricLabel } from "@/lib/biometric";
import {
  clearCredentials,
  getCredentials,
  isBiometricLoginEnabled,
  saveCredentials,
} from "@/lib/biometricCredentials";

type SignOutOptions = {
  /**
   * Also delete the credentials saved for biometric sign-in.
   *
   * Only pass this from a Logout the user deliberately pressed. The automatic
   * sign-outs below (expired session, rejected token) must leave the saved
   * credentials alone, or every token expiry would silently un-enrol the user
   * from biometric login — which is the one thing that makes signing back in
   * painless.
   */
  clearBiometric?: boolean;
};

type SupabaseContextProps = {
  user: User | null;
  session: Session | null;
  initialized?: boolean;
  isDeepLink?: boolean;
  biometricAvailable: boolean;
  biometricEnabled: boolean;
  biometricLabel: BiometricLabel;
  signUp: (email: string, password: string) => Promise<{
    user: User | null;
    session: Session | null;
  }>
  signInWithPassword: (email: string, password: string) => Promise<User | null>;
  signInWithBiometric: () => Promise<User | null>;
  enableBiometric: (email: string, password: string) => Promise<void>;
  disableBiometric: () => Promise<void>;
  signOut: (options?: SignOutOptions) => Promise<void>;
  refetchUser: () => Promise<void>;
};

type SupabaseProviderProps = {
  children: React.ReactNode;
};

export const SupabaseContext = createContext<SupabaseContextProps | undefined>(
  undefined
);

export const SupabaseProvider = ({ children }: SupabaseProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [isDeepLink, setIsDeepLink] = useState<boolean>(false);
  const [biometricAvailable, setBiometricAvailable] = useState<boolean>(false);
  const [biometricEnabled, setBiometricEnabled] = useState<boolean>(false);
  const [biometricLabel, setBiometricLabel] = useState<BiometricLabel>('biometric unlock');

  const segments = useSegments()[0];
  const router = useRouter();

  const signUp = async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) {
      throw error;
    }
    return data;
  };

  const signInWithPassword = async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      throw error;
    }
    return data.user;
  };

  const signInWithBiometric = async () => {
    // Reading credentials triggers the OS biometric prompt. A rejection here
    // most likely means the user cancelled the prompt or hardware refused —
    // we must NOT clear saved credentials in that case.
    let creds;
    try {
      creds = await getCredentials();
    } catch (error: any) {
      const wrapped = new Error(error?.message ?? 'Biometric authentication cancelled');
      (wrapped as any).biometricCancelled = true;
      throw wrapped;
    }

    if (!creds) {
      // Nothing stored, or the keychain entry was invalidated (e.g. biometric
      // enrolment changed on iOS). Reset state so the UI falls back cleanly.
      await clearCredentials();
      setBiometricEnabled(false);
      const err = new Error('No saved credentials available');
      (err as any).biometricMissing = true;
      throw err;
    }

    try {
      const signedInUser = await signInWithPassword(creds.email, creds.password);
      return signedInUser;
    } catch (error: any) {
      // Sign-in failed with stored creds — treat as stale, wipe, and surface
      // so the UI can ask the user to sign in manually.
      await clearCredentials();
      setBiometricEnabled(false);
      (error as any).biometricStale = true;
      throw error;
    }
  };

  const enableBiometric = async (email: string, password: string) => {
    await saveCredentials(email, password);
    setBiometricEnabled(true);
  };

  const disableBiometric = async () => {
    await clearCredentials();
    setBiometricEnabled(false);
  };

  const signOut = async (options?: SignOutOptions) => {
    // Detach the OneSignal external_id from this device's subscription so
    // future pushes for this user never reach this device. If we skip this,
    // the subscription stays aliased to the user's external_id forever and
    // accumulates as a "ghost" subscription on the OneSignal account.
    //
    // NOTE: previously this used `typeof window === 'undefined'` as a native
    // guard, but `window` is defined in React Native's JS environment, so
    // that check was always false and logout never actually ran.
    if (Platform.OS !== 'web') {
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { OneSignal } = require('react-native-onesignal');
        try { OneSignal.logout(); } catch { }
      } catch { }
    }

    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }

    // A deliberate logout drops the saved email/password too. Without this the
    // sign-in screen still offers biometric unlock, which puts the user
    // straight back into the account they just left — the exact trap for
    // someone who signed up under the wrong email.
    if (options?.clearBiometric) {
      await clearCredentials();
      setBiometricEnabled(false);
    }
  };

  const parseSupabaseUrl = (url: string) => {
    let parsedUrl = url;
    if (url.includes("#")) {
      parsedUrl = url.replace("#", "?");
    }

    return parsedUrl;
  };

  const refetchUser = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error) {
        // If we get an error, the session might be expired - clear it
        if (error.message.includes('expired') || error.message.includes('invalid')) {
          await signOut();
        }
        throw error;
      }
      setUser(data.user as User);
    } catch (err) {
      console.error('Failed to refetch user:', err);
      if (segments !== '(auth)') {
        // Redirect to auth if not already there
        router.replace('/(auth)');
      }
    }
  }, [router, segments]);

  // Check session validity periodically
  useEffect(() => {
    if (!user) return;

    const checkSessionInterval = setInterval(async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();

        // If no session or expired token, sign out and redirect
        if (!currentSession ||
          (currentSession.expires_at && new Date(currentSession.expires_at * 1000) < new Date())) {
          await signOut();
          router.replace('/(auth)');
        }
      } catch (error) {
        console.error('Session check failed:', error);
      }
    }, 60000); // Check every minute

    return () => clearInterval(checkSessionInterval);
  }, [user, router]);

  useEffect(() => {
    const handleDeepLink = async (url: string) => {
      const transformedUrl = parseSupabaseUrl(url);
      const parsedUrl = Linking.parse(transformedUrl);

      const access_token = parsedUrl.queryParams?.access_token;
      const refresh_token = parsedUrl.queryParams?.refresh_token;

      try {
        if (typeof access_token === "string" && typeof refresh_token === "string") {
          const { data, error } = await supabase.auth.setSession({
            access_token,
            refresh_token
          })
          setUser(data.user as User);
          setSession(data.session);
          setIsDeepLink(true);

          switch (parsedUrl.hostname) {
            case "reset-password":
              router.replace("/(account)");
              break;
            case "confirm-email":
              router.replace({ pathname: "/(onboarding)", params: { emailConfirmed: 'true' } });
              break;
            case "notifications":
              router.replace("/(app)/notifications");
              break;
            default:
              router.replace(url as never);
          }
        }
      } catch (error: any) {
        console.error('Failed to set session:', error.message
        )
      }
    };

    Linking.addEventListener('url', ({ url }) => handleDeepLink(url));

    Linking.getInitialURL().then(url => {
      if (url) {
        handleDeepLink(url);
      }
    });
  }, []);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session ? session.user as User : null);
      setInitialized(true);

      // Redirect on sign out
      if (event === 'SIGNED_OUT') {
        router.replace('/(auth)');
      }
    });
    return () => {
      data.subscription.unsubscribe();
    };
  }, [router]);

  // Hydrate biometric availability + enabled state once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [availability, enabled] = await Promise.all([
        getBiometricAvailability(),
        isBiometricLoginEnabled(),
      ]);
      if (cancelled) return;
      setBiometricAvailable(availability.available);
      setBiometricLabel(availability.label);
      setBiometricEnabled(enabled);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Push registration self-healing is handled by PushRegistrationProvider.

  // Push notification click handling.
  //
  // OneSignal v5 does NOT auto-launch the URL attached to a notification
  // (`app_url` from the server side). The SDK fires a click event and the
  // app is responsible for navigating. Without this listener, tapping any
  // push just opens the app on the default route (calendar) — the URL the
  // notification was sent with is silently dropped.
  //
  // We support both `event.notification.launchURL` (preferred) and
  // `event.result.url` (older payload shape) for forward/backward safety.
  // URLs are parsed with expo-linking so we get scheme/host/path back; we
  // currently route `crew://messages/<id>` and `crew://notifications`, and
  // fall back to a generic /<host>/<path> push for anything else so future
  // app_url shapes do something sensible without code changes here.
  //
  // Cold-start handling: if the app was launched from a tap and the user
  // session hasn't been restored yet, we stash the URL in a ref and the
  // effect that watches `user` replays it as soon as auth is available.
  // Without this, the deep link races with the auth-redirect logic in
  // (app)/_layout and is lost.
  const pendingDeepLinkRef = useRef<string | null>(null);

  const navigateToNotificationUrl = useCallback((url: string) => {
    try {
      const parsed = Linking.parse(url);
      const host = parsed.hostname || '';
      const path = (parsed.path || '').replace(/^\/+/, '');

      if (host === 'messages' && path) {
        router.push(`/(app)/messages/${path}` as never);
        return;
      }
      if (host === 'notifications') {
        router.push('/(app)/notifications' as never);
        return;
      }
      if (host) {
        const fallback = path ? `/${host}/${path}` : `/${host}`;
        router.push(fallback as never);
        return;
      }
    } catch (err) {
      console.warn('Failed to navigate to notification URL', url, err);
    }
  }, [router]);

  // Replay a queued deep link as soon as auth becomes available.
  useEffect(() => {
    if (!user?.id) return;
    const pending = pendingDeepLinkRef.current;
    if (!pending) return;
    pendingDeepLinkRef.current = null;
    navigateToNotificationUrl(pending);
  }, [user?.id, navigateToNotificationUrl]);

  useEffect(() => {
    if (Platform.OS === 'web') return;

    let removeListener: (() => void) | undefined;
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { OneSignal } = require('react-native-onesignal');

      const handler = (event: any) => {
        const url: string | undefined =
          event?.notification?.launchURL ?? event?.result?.url;
        if (typeof url !== 'string' || url.length === 0) return;

        if (!user?.id) {
          pendingDeepLinkRef.current = url;
          return;
        }
        navigateToNotificationUrl(url);
      };

      try { OneSignal.Notifications.addClickListener(handler); } catch { }
      removeListener = () => {
        try { OneSignal.Notifications.removeClickListener?.(handler); } catch { }
      };
    } catch { }

    return () => {
      if (removeListener) removeListener();
    };
  }, [user?.id, navigateToNotificationUrl]);

  useEffect(() => {
    if (!initialized) return;

    if (isDeepLink)
      return;

    // Auth-boundary redirects only:
    //  - signed out & on a protected route       → send to auth
    //  - signed in  & on the auth route          → send to app
    //  - signed in  & on (account) without deep
    //    link context (i.e. NOT a password-reset
    //    flow)                                   → send to app
    //
    // Once signed in, let per-group layouts (e.g. (app)/_layout deciding
    // onboarding vs holding vs drawer) handle internal routing. Otherwise
    // we fight with their <Redirect> calls and create an infinite loop.
    //
    // (account) is only valid when entered via a deep link. expo-router 5
    // restores navigation state across cold starts, so without this guard
    // the app can launch directly into the password-reset screen.
    if (!session && segments !== "(auth)") {
      router.replace("/(auth)");
    } else if (session && (segments === "(auth)" || segments === "(account)")) {
      router.replace("/(app)");
    }
  }, [initialized, session, segments]);

  return (
    <SupabaseContext.Provider
      value={{
        user,
        session,
        initialized,
        isDeepLink,
        biometricAvailable,
        biometricEnabled,
        biometricLabel,
        signUp,
        signInWithPassword,
        signInWithBiometric,
        enableBiometric,
        disableBiometric,
        signOut,
        refetchUser,
      }}
    >
      {children}
    </SupabaseContext.Provider>
  );
};